import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { motion } from 'framer-motion';
import { Column, Row, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { relationshipModel } from "../../../../../models";
import { getStepName } from "../../../ClientOnboarding.common.utils";
import { getReachableInnerSteps } from "../../ClientOnboardingV2.utils";
import styles from "./Sidebar.module.scss";
import { INDICATOR_HEIGHT, StepState, calculateIndicatorPosition, getStepState } from "./Sidebar.utils";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const GetSidebarTID = parentTestId => getTestIds({
  parentTestId
});
export const Sidebar = _ref => {
  let {
    testId,
    currentStep,
    onStepClick,
    relationship,
    visitedSteps
  } = _ref;
  const {
    SidebarTestIds
  } = useMemo(() => GetSidebarTID(testId), [testId]);
  const [indicatorStyle, setIndicatorStyle] = useState({
    top: 0,
    height: 0
  });
  const stepRefs = useRef([]);
  const reachableSteps = useMemo(() => getReachableInnerSteps(relationship), [relationship]);
  const vendorLogoUrl = relationshipModel.vendorLogoUrl({
    vendor: relationship.vendor
  });
  const currentStepIndex = reachableSteps.indexOf(currentStep);
  useLayoutEffect(() => {
    const currentRef = stepRefs.current[currentStepIndex];
    if (currentRef) {
      setIndicatorStyle(calculateIndicatorPosition(currentRef, INDICATOR_HEIGHT));
    }
  }, [currentStepIndex]);
  const handleStepClick = (step, state) => {
    if (state !== StepState.Disabled) {
      onStepClick(step);
    }
  };
  return /*#__PURE__*/_jsxs(Column, {
    width: 288,
    padding: 40,
    gap: 32,
    "data-testid": SidebarTestIds.Root(),
    children: [/*#__PURE__*/_jsx(Row, {
      children: vendorLogoUrl ? /*#__PURE__*/_jsx("img", {
        src: vendorLogoUrl,
        alt: relationship.vendor.name,
        className: styles.vendorLogo,
        "data-testid": SidebarTestIds.VendorLogo()
      }) : /*#__PURE__*/_jsx(Typography, {
        className: styles.vendorName,
        variant: "h5",
        "data-testid": SidebarTestIds.VendorName(),
        children: relationship.vendor.name
      })
    }), /*#__PURE__*/_jsxs(Column, {
      className: styles.stepsList,
      "data-testid": SidebarTestIds.StepsList(),
      children: [/*#__PURE__*/_jsx(motion.div, {
        className: styles.indicator,
        initial: false,
        animate: {
          top: indicatorStyle.top,
          height: indicatorStyle.height
        },
        transition: {
          type: 'spring',
          stiffness: 500,
          damping: 35
        }
      }), reachableSteps.map((step, index) => {
        const state = getStepState(step, currentStep, visitedSteps);
        const isSelected = state === StepState.Selected;
        const isDisabled = state === StepState.Disabled;
        const isEnabled = state === StepState.Enabled;
        return /*#__PURE__*/_jsx(Typography, {
          variant: "paragraph2",
          weight: isSelected ? 'normal' : 'light',
          skin: isDisabled ? 'disabled' : isSelected ? 'primary' : 'standard',
          ref: el => stepRefs.current[index] = el,
          className: cn(styles.stepItem, {
            [styles.enabled]: isEnabled
          }),
          onClick: () => handleStepClick(step, state),
          "data-testid": SidebarTestIds.StepItem(step),
          children: getStepName(step, relationship)
        }, step);
      })]
    })]
  });
};